import type { ReactElement } from 'react';
import { useT } from '../lib/i18n';

/**
 * Why there is nothing on screen: the browser has no connection, and everything
 * this application shows lives on the server.
 *
 * Shown in two places, which is why it is a component rather than a paragraph.
 * The list uses it when the list itself cannot be fetched. `RequireAuth` uses it
 * when even `GET /api/auth/me` is paused — an application opened from a home
 * screen with no connection would otherwise sit on a spinner for ever, since the
 * sign-in screen it would fall back to needs the same network.
 *
 * The service worker caches nothing under `/api/` on purpose: that is what keeps
 * one account's data from reaching the next person to open the application on the
 * same device. Saying so is the honest alternative to an empty list.
 */
export function OfflineNotice(): ReactElement {
  const t = useT();

  return (
    <div className="rounded-lg border border-line-strong bg-surface/60 px-5 py-8 text-center text-ink-400">
      <svg
        viewBox="0 0 24 24"
        className="mx-auto size-7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M3 3l18 18M8.5 16.5a5 5 0 0 1 7 0M5 13a10 10 0 0 1 4-2.5M19 13a10 10 0 0 0-7-3M12 20h.01" />
      </svg>
      <p className="mt-3 font-medium text-ink-900">{t('todos.offlineTitle')}</p>
      <p className="mx-auto mt-1 max-w-prose text-sm text-ink-500">{t('todos.offlineBody')}</p>
    </div>
  );
}
